/**
 * ShopSettingsView - Shop settings management for administration
 * Extracted from AdminShopTab.js in Phase 4D-5
 * 
 * Manages:
 * - Shop logo upload and preview
 * - Shop name configuration
 * - Shop tagline configuration
 * - Live header preview
 * 
 * Settings impact: shop_name and tagline appear on public shop page header
 * No payment, pricing, tax, or shipping settings are managed here
 */
import React from 'react';
import { Plus, Trash2, Loader2, Image } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

/**
 * @param {Object} props
 * @param {Object} props.settingsForm - Current form state
 * @param {string} props.settingsForm.shop_name - Shop name value
 * @param {string} props.settingsForm.tagline - Shop tagline value
 * @param {string} props.settingsForm.logo_url - Logo URL (or empty string)
 * @param {Function} props.onSettingsFormChange - Callback to update form state
 * @param {string} props.apiUrl - API base URL for logo src resolution
 * @param {React.RefObject} props.logoInputRef - Ref for hidden file input
 * @param {boolean} props.uploadingLogo - Whether logo is being uploaded
 * @param {boolean} props.savingSettings - Whether settings are being saved
 * @param {Function} props.onLogoUpload - Handler for logo file selection
 * @param {Function} props.onSaveSettings - Handler for save button click
 */
export function ShopSettingsView({
  settingsForm,
  onSettingsFormChange,
  apiUrl,
  logoInputRef,
  uploadingLogo,
  savingSettings,
  onLogoUpload,
  onSaveSettings
}) {
  // Helper to resolve logo URL (handle relative vs absolute)
  const getLogoSrc = (url) => {
    if (!url) return '';
    return url.startsWith('/') ? `${apiUrl}${url}` : url;
  };

  // Helper to update a single form field
  const updateField = (field, value) => {
    onSettingsFormChange(prev => ({ ...prev, [field]: value }));
  };

  // Helper to clear logo
  const clearLogo = () => {
    updateField('logo_url', '');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shop Settings</CardTitle>
        <CardDescription>Customize your shop&apos;s name, logo, and appearance</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo Upload */}
        <div>
          <Label className="mb-3 block">Shop Logo</Label>
          <div className="flex items-start gap-6">
            {/* Logo Preview */}
            <div className="relative">
              {settingsForm.logo_url ? (
                <div className="relative group">
                  <img
                    src={getLogoSrc(settingsForm.logo_url)}
                    alt="Shop Logo"
                    className="h-24 w-auto max-w-[200px] object-contain border rounded-lg bg-slate-50 p-2"
                  />
                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="h-24 w-32 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50">
                  <Image className="h-8 w-8 text-slate-300" />
                </div>
              )}
            </div>
            
            {/* Upload Button */}
            <div className="flex-1">
              <Button
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {settingsForm.logo_url ? 'Change Logo' : 'Upload Logo'}
                  </>
                )}
              </Button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={onLogoUpload}
                className="hidden"
              />
              <p className="text-xs text-slate-500 mt-2">
                Recommended: PNG or SVG with transparent background. Max height 112px on shop page.
              </p>
            </div>
          </div>
        </div>
        
        {/* Shop Name */}
        <div>
          <Label htmlFor="shop_name">Shop Name</Label>
          <Input
            id="shop_name"
            value={settingsForm.shop_name}
            onChange={(e) => updateField('shop_name', e.target.value)}
            placeholder="Love 2 Line Dance"
            className="max-w-md"
          />
          <p className="text-xs text-slate-500 mt-1">
            This appears as the main heading on your shop page
          </p>
        </div>
        
        {/* Tagline */}
        <div>
          <Label htmlFor="tagline">Tagline</Label>
          <Input
            id="tagline"
            value={settingsForm.tagline}
            onChange={(e) => updateField('tagline', e.target.value)}
            placeholder="Quality merchandise for line dance enthusiasts"
            className="max-w-lg"
          />
          <p className="text-xs text-slate-500 mt-1">
            A short description shown below the shop name
          </p>
        </div>
        
        {/* Preview */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-violet-600 to-purple-700 text-white py-8 px-6 text-center">
            {settingsForm.logo_url && (
              <img
                src={getLogoSrc(settingsForm.logo_url)}
                alt="Logo preview"
                className="h-16 mx-auto mb-3 object-contain"
              />
            )}
            <h3 className="text-2xl font-bold mb-2">{settingsForm.shop_name || 'Shop Name'}</h3>
            <p className="text-violet-100 text-sm">{settingsForm.tagline || 'Your tagline here'}</p>
          </div>
          <div className="bg-slate-100 text-center py-2 text-xs text-slate-500">
            Preview of shop header
          </div>
        </div>
        
        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onSaveSettings} disabled={savingSettings}>
            {savingSettings && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default ShopSettingsView;
